import { useState } from "react";
import { Loader2, Search, Plus, Save, ClipboardList } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const STATUSES = ["scheduled", "completed", "cancelled"];

const RecordRow = ({ record, onSaved }) => {
  const [status, setStatus] = useState(record.status);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/service-records/${record.id}`, { status });
      toast.success("Record updated");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <li data-testid={`admin-record-${record.id}`} className="py-3 first:pt-0 last:pb-0">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="font-medium text-brand-ink">{record.service}</p>
          <p className="text-xs text-zinc-500">{record.date}</p>
          {record.notes && <p className="text-sm text-zinc-600 mt-0.5">{record.notes}</p>}
        </div>
        <div className="flex items-center gap-2">
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid={`admin-record-status-${record.id}`} className="rounded-sm h-9 w-36 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <button onClick={save} disabled={saving || status === record.status} data-testid={`admin-record-save-${record.id}`}
            className="bg-brand hover:bg-brand-dark disabled:opacity-40 text-white p-2 rounded-sm transition-colors duration-200" aria-label="Save status">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          </button>
        </div>
      </div>
    </li>
  );
};

const AddRecordForm = ({ loadedEmail, onAdded }) => {
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ service: "", date: "", status: "completed", notes: "" });

  const addRecord = async (e) => {
    e.preventDefault();
    setAdding(true);
    try {
      await api.post("/admin/service-records", { customer_email: loadedEmail, ...form });
      toast.success("Service record added");
      setForm({ service: "", date: "", status: "completed", notes: "" });
      onAdded();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setAdding(false);
    }
  };

  return (
    <form onSubmit={addRecord} className="mt-5 bg-brand-subtle border border-zinc-200 rounded-sm p-4">
      <p className="font-heading font-bold text-sm text-brand-ink mb-3 flex items-center gap-1.5">
        <Plus className="w-4 h-4 text-brand" /> Add a service record for this customer
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Input data-testid="admin-add-service-input" required placeholder="Service (e.g. AC Repair — Capacitor)"
          value={form.service} onChange={(e) => setForm({ ...form, service: e.target.value })} className="rounded-sm bg-white" />
        <Input data-testid="admin-add-date-input" required type="date"
          value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="rounded-sm bg-white" />
        <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
          <SelectTrigger data-testid="admin-add-status-select" className="rounded-sm bg-white"><SelectValue /></SelectTrigger>
          <SelectContent>
            {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <Textarea data-testid="admin-add-notes-input" placeholder="Job notes (what was done, parts used, readings...)"
        value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-sm bg-white mt-3" rows={2} />
      <button type="submit" disabled={adding} data-testid="admin-add-record-btn"
        className="mt-3 bg-brand hover:bg-brand-dark disabled:opacity-60 text-white font-bold px-6 py-2.5 rounded-sm transition-colors duration-200 text-sm flex items-center gap-2">
        {adding && <Loader2 className="w-4 h-4 animate-spin" />} Add Record
      </button>
    </form>
  );
};

const RecordsLookupForm = ({ email, setEmail, loading, onSubmit }) => (
  <form onSubmit={onSubmit} className="flex gap-2">
    <Input data-testid="admin-records-email-input" type="email" required placeholder="Customer email (e.g. demo@centralairdepot.com)"
      value={email} onChange={(e) => setEmail(e.target.value)} className="rounded-sm" />
    <button type="submit" disabled={loading} data-testid="admin-records-load-btn"
      className="bg-brand hover:bg-brand-dark disabled:opacity-60 text-white font-bold px-5 rounded-sm flex items-center gap-2 transition-colors duration-200 text-sm shrink-0">
      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} Load
    </button>
  </form>
);

const RecordsResults = ({ customer, records, loadedEmail, reload }) => (
  <div className="mt-5">
    <p data-testid="admin-records-customer" className="text-sm font-medium text-brand-ink">
      {customer ? `${customer.name} — ${loadedEmail}` : <span className="text-brand-red">No account found for {loadedEmail} (records can still be added; they'll appear when the customer registers with this email)</span>}
    </p>

    {records.length === 0 ? (
      <p data-testid="admin-records-empty" className="text-sm text-zinc-500 mt-3">No service records for this customer yet.</p>
    ) : (
      <ul className="divide-y divide-zinc-100 mt-3 border-t border-zinc-100">
        {records.map((r) => <RecordRow key={r.id} record={r} onSaved={reload} />)}
      </ul>
    )}

    <AddRecordForm loadedEmail={loadedEmail} onAdded={reload} />
  </div>
);

export const AdminServiceRecords = () => {
  const [email, setEmail] = useState("");
  const [loadedEmail, setLoadedEmail] = useState("");
  const [customer, setCustomer] = useState(null);
  const [records, setRecords] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async (targetEmail) => {
    const em = (targetEmail || email).trim().toLowerCase();
    if (!em) return;
    setLoading(true);
    try {
      const r = await api.get("/admin/service-records", { params: { customer_email: em } });
      setRecords(r.data.records);
      setCustomer(r.data.customer);
      setLoadedEmail(em);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="admin-service-records-card" className="bg-white border border-zinc-200 p-6 rounded-sm mt-6">
      <h2 className="font-heading font-bold text-lg text-brand-ink mb-1 flex items-center gap-2">
        <ClipboardList className="w-5 h-5 text-brand" /> Customer Service Records (Admin)
      </h2>
      <p className="text-sm text-zinc-600 mb-4">Look up a customer by email to add a completed job or update the status of an existing record.</p>
      <RecordsLookupForm email={email} setEmail={setEmail} loading={loading} onSubmit={(e) => { e.preventDefault(); load(); }} />
      {records !== null && (
        <RecordsResults customer={customer} records={records} loadedEmail={loadedEmail} reload={() => load(loadedEmail)} />
      )}
    </div>
  );
};
